import React, { useMemo, useState, FC, ReactNode, useEffect, useRef } from 'react'
import { GetStaticPaths, GetStaticPathsContext, GetStaticPropsContext, NextPage, GetServerSidePropsContext } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { Element } from 'react-scroll'
import { SwiperSlide } from 'swiper/react'
import useSWR from 'swr'
import clsx from 'clsx'
import { useMobileDetector } from '@hooks/use-mobile-detector'
import { isDataEmpty } from '@/common/misc'
import {
  getSolutionDetails,
  getSolutionDetailsInternal,
  SolutionDetailsData,
  TSecondSolutionItem,
} from '@service/solution.service'
import { useChinaDetector, findRegionByLocale } from '@common/region-locale'
import retriveMessages, { LocaleType } from '@messages/message'
import PdSwiper from '@components/pd-swiper/pd-swiper'
import PdNavbar from '@components/pd-navbar/pd-navbar'
import PdSimpleNavbar, { SimpleNavbarProps } from '@components/pd-simple-navbar/pd-simple-navbar'
import PdFooter from '@components/pd-footer/pd-footer'
import PdSection from '@components/pd-section/pd-section'
import PdImage from '@components/pd-image/pd-image'
import PdMedia from '@components/pd-media/pd-media'
import PdDataCard from '@components/pd-data-card/pd-data-card'
import DownloadCard from '@components/standard/download-card'
import SlidingTabContent from '@components/standard/sliding-tab-content'
import PdSwipeableTabs from '@components/pd-swipeable-tabs/pd-swipeable-tabs'
import PdProductFeature from '@components/pd-product-feature/pd-product-feature'
import PdNews from '@components/pd-news/pd-news'
import ContactSection from '@components/pd-contactus-section/pd-contactus-section'
import NewsDisplay from './news-display.component'
import AngleRight from '@components/pd-angle-right/pd-angle-right'
import SlidingTabs from '@components/standard/pd-sliding-tabs'
import { withSEO } from '@/common/with-seo'

// 全局变量，记录上一次的滚动位置
let gPrevScrollTop: number = 0

interface IRelatedCaseSectionProps {
  title: string
  desc: string
  country: string
  industryName: string
  productName: string[]
  mediaUrls: string[]
  docId: string
  tabTitle: string
}

interface IMoreApplicationsCardProps {
  docId: string
  mediaUrl: string
  title: string
  content: string
  jumpUrl: string
}

const RelatedCaseSection: FC<{ data: IRelatedCaseSectionProps }> = ({
  data: { title, country, desc, industryName, productName = [], mediaUrls, docId, tabTitle },
}) => {
  const t = useTranslations()
  const parentRef = useRef<HTMLDivElement>(null)
  const childRefs = useRef<Array<HTMLSpanElement | null>>([])
  const [overflowIndex, setOverflowIndex] = useState(-1)

  // 计算溢出的索引
  const calculateOverflow = () => {
    if (!parentRef.current || childRefs.current.length === 0) return

    const parentWidth = parentRef.current.offsetWidth
    const parentStyles = !!window && window.getComputedStyle(parentRef.current)
    const gap = parseFloat(parentStyles.gap) || 0
    let totalWidth = 0

    for (let i = 0; i < childRefs.current.length; i++) {
      const child = childRefs.current[i]

      if (!child) break
      totalWidth += child.offsetWidth + (i > 0 ? gap : 0)
      if (totalWidth + (window?.innerWidth < 1024 ? 24 : 64) > parentWidth) break

      setOverflowIndex(i)
    }
  }

  // 初始化监听父容器和子元素尺寸变化
  useEffect(() => {
    const parent = parentRef.current
    if (!parent) return

    // 初始计算
    calculateOverflow()
  }, [calculateOverflow])

  const imageUrl = useMemo(
    () => mediaUrls?.find((url) => ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(url.split('.').pop() || '')) || '',
    [mediaUrls]
  )

  const showChildDoms = useMemo<ReactNode[]>(() => {
    const shouldShowProductName: string[] = productName.slice(0, overflowIndex + 1)

    return shouldShowProductName?.map((tag, index) => (
      <span
        key={index}
        className="border px-3 py-0.5 rounded-sm outline-1 outline-offset-[-1px] outline-zinc-300 justify-start items-center gap-1 laptop:gap-2"
      >
        {tag}
      </span>
    ))
  }, [overflowIndex, productName])

  return (
    <Link href={`/case-studies/${docId}`}>
      <div
        className={clsx(
          'flex bg-white',
          'w-auto flex-col gap-0 p-0 mx-4',
          'laptop:max-w-300 laptop:flex-row-reverse laptop:gap-12 laptop:p-6 laptop:mx-auto'
        )}
      >
        <div className={clsx('flex-shrink-0 flex-grow-0', 'w-full h-228px', 'laptop:w-693px laptop:h-462px')}>
          <PdImage src={imageUrl} object="cover" enableMask={false} width={'100%'} height={'100%'} />
        </div>
        <div
          className={clsx('flex flex-col justify-between', 'h-fit p-5 gap-5', 'laptop:h-auto laptop:p-0 laptop:gap-0')}
        >
          <div className={clsx('')}>
            <div className={clsx('text-black/90 font-semibold', 'text-15', 'laptop:text-2xl')}>{title}</div>
            <div
              className={clsx(
                'font-normal',
                'text-black/60 text-15 mt-3',
                'laptop:text-black/90 laptop:text-base laptop:mt-2'
              )}
            >
              {desc}
            </div>
          </div>
          <div className={clsx('flex flex-col', 'gap-3', 'laptop:gap-6 laptop:mt-6')}>
            <div className={clsx('text-black/60 font-normal', 'text-15', 'laptop:text-sm')}>
              <span>{country}</span>
              <span className="ml-2 laptop:ml-4">{industryName}</span>
            </div>

            <div
              ref={parentRef}
              className="mt-auto flex flex-wrap gap-2 text-black/60 text-sm font-normal leading-snug"
            >
              {overflowIndex === -1 &&
                productName?.map((tag, index) => (
                  <span
                    key={index}
                    ref={(el) => (childRefs.current[index] = el)}
                    className="border px-3 py-0.5 rounded-sm outline-1 outline-offset-[-1px] outline-zinc-300 justify-start items-center gap-1 laptop:gap-2"
                  >
                    {tag}
                  </span>
                ))}
              {overflowIndex !== -1 && showChildDoms}
              {productName.length !== overflowIndex + 1 && (
                <span className="flex items-center">
                  <span className="w-0.5 h-0.5 bg-black/60 rounded-full" />
                  <span className="w-0.5 h-0.5 bg-black/60 rounded-full mx-0.5" />
                  <span className="w-0.5 h-0.5 bg-black/60 rounded-full" />
                </span>
              )}
            </div>

            <div className={clsx('mt-1', 'laptop:mt-0')}>
              <Link href={`/case-studies/${docId}`}>
                <a className={['mt-6', 'laptop:mt-0', 'text-sm text-pdblue'].join(' ')}>
                  {t('KnowMore')}
                  <AngleRight theme="PUDUBLUE" size="4x8" />
                </a>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}

const MoreApplicationsCard: FC<Partial<IMoreApplicationsCardProps & { boxClassName?: string }>> = ({
  docId = '',
  title = '',
  jumpUrl = '',
  content,
  mediaUrl = '',
  boxClassName,
}) => {
  return (
    <Link href={`/solutions/${docId}`}>
      <div
        className={clsx(
          'group relative cursor-pointer bg-white h-full',
          'flex flex-col',
          'w-72',
          'laptop:w-110',
          boxClassName
        )}
      >
        <div className={clsx('w-full flex-shrink-0 flex-grow-0', 'h-192px', 'laptop:h-293px')}>
          <PdMedia src={mediaUrl} alt={title} object="cover" mask={false} showShadow={false} />
        </div>
        <div className={clsx('flex-1 flex flex-col justify-between', 'p-3', 'laptop:p-6')}>
          <div className={clsx('w-full h-fit')}>
            <h3 className={clsx('text-black font-semibold line-clamp-2', 'text-15 mb-1', 'laptop:text-xl laptop:mb-2')}>
              {title}
            </h3>
            <p className={clsx('text-black/60 font-normal line-clamp-3', 'text-15', 'laptop:text-sm')}>{content}</p>
          </div>
          <div className={clsx('w-full mt-3 laptop:mt-6')}>
            <div
              className={clsx(
                'laptop:opacity-0 opacity-100 group-hover:opacity-100 transition-opacity duration-300',
                'ml-auto',
                'w-6 h-6'
              )}
            >
              <PdImage src="/icons/icon_indicator.svg" alt="indicator" enableMask={false} width="100%" height="100%" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}

const Solution: NextPage<{ data: SolutionDetailsData }> = ({ data: staticData }) => {
  const t = useTranslations()
  const {
    locale,
    pathname,
    query: { solutionTitle },
  } = useRouter()
  const inChina = useChinaDetector()
  const isMobileDevice = useMobileDetector()
  const [activeSecondSolutionItem, setActiveSecondSolutionItem] = useState<number>(0)

  // 当 staticData 没有数据时，使用 useSWR 客户端获取数据兜底
  const shouldFetch = !staticData || isDataEmpty(staticData)
  const { data: swrData } = useSWR(
    shouldFetch && solutionTitle
      ? { docId: solutionTitle as string, region: findRegionByLocale(locale as string) }
      : null,
    getSolutionDetails
  )

  const data = (shouldFetch ? swrData : staticData) || staticData

  const navbarMenu = useMemo(() => {
    const menu = {
      containerId: 'main',
      title: data?.title || (solutionTitle as string) || '',
      menus: [
        { name: data?.middleSmallTitle || '', element: 'section-1' },
        // 中部banner
        !!data?.middleBanner ? { name: data?.middleBanner?.smallTitle || '', element: 'section-2' } : null,
        // 各级场景
        !!data?.secondSolutionModule?.secondSolutionItem?.length
          ? { name: data?.secondSolutionModule?.smallTitle || '', element: 'section-3' }
          : null,
        // 下载板块
        !!data?.downloadItems?.length ? { name: data?.downloadSmallTitle || '', element: 'section-4' } : null,
        // 更多应用
        !!data?.otherSolutionItems?.length ? { name: data?.otherSolutionSmallTitle || '', element: 'section-5' } : null,
      ].filter((item) => item !== null),
    }

    return menu
  }, [data])

  /* 机器人如何赋能 */
  const dataCardData = useMemo(() => {
    if (!data || !data?.middleItems) {
      return []
    }

    return data.middleItems.map((item) => ({
      icon: item.mediaUrl,
      label: item.title,
      value: item.desc,
    }))
  }, [data])

  const activeData = useMemo<TSecondSolutionItem | undefined>(() => {
    return data?.secondSolutionModule?.secondSolutionItem?.[activeSecondSolutionItem]
  }, [data, activeSecondSolutionItem])

  // 仅多个二级行业时展示tab切换栏
  const showTabs = useMemo<boolean>(() => {
    return (data?.secondSolutionModule?.secondSolutionItem?.length || 0) > 1
  }, [data])

  const handleActiveTabChange = (id: number) => {
    setActiveSecondSolutionItem(id)
  }

  function onMainScroll(evt: React.UIEvent<HTMLDivElement, UIEvent>) {
    const target = evt.target as HTMLElement
    const solutionPdNavbar = document.getElementById('solution-pd-navbar') // 一级导航栏
    if (!solutionPdNavbar) {
      return
    }

    const solutoinSimpleNavbar = document.getElementById('solution-simple-navbar') // 二级导航栏
    if (!solutoinSimpleNavbar) {
      return
    }

    const prevScrollTop = gPrevScrollTop
    const currScrollTop = target.scrollTop
    gPrevScrollTop = currScrollTop

    // means solutionSimpleNavbar is invisible
    if (solutoinSimpleNavbar.offsetWidth === 0) {
      solutionPdNavbar.style.transform = `translateY(0px)` // recovery transform
      return
    }

    /* 如果向上滚动，则显示一二级导航栏 */
    const scrollUP = prevScrollTop > currScrollTop

    if (scrollUP) {
      whenScrollUP(currScrollTop, solutionPdNavbar, solutoinSimpleNavbar)
    } else {
      whenScrollDown(currScrollTop, solutionPdNavbar, solutoinSimpleNavbar)
    }
  }

  function whenScrollUP(currScrollTop: number, navbar: HTMLElement, simpleNavbar: HTMLElement) {
    const navbarHeight = navbar.offsetHeight
    navbar.style.transform = `translateY(0)`
    simpleNavbar.style.transform = `translateY(${currScrollTop > navbarHeight ? navbarHeight : currScrollTop}px)`
  }

  function whenScrollDown(currScrollTop: number, navbar: HTMLElement, simpleNavbar: HTMLElement) {
    const navbarHeight = navbar.offsetHeight

    if (currScrollTop <= navbarHeight) {
      navbar.style.transform = `translateY(-${currScrollTop}px)`
      simpleNavbar.style.transform = `translateY(0)`
    } else {
      navbar.style.transform = `translateY(-${navbarHeight}px)`
      simpleNavbar.style.transform = `translateY(0)`
    }
  }

  return (
    <div className="container-fluid w-full h-screen">
      {/* SEO handled by _app.tsx */}

      <main
        id="main"
        className="container-fluid w-full h-full overflow-y-auto bg-pdbg will-change-scroll"
        onScroll={onMainScroll}
      >
        {/* pd-navbar */}
        <div
          id="solution-pd-navbar"
          className={[
            // "laptop:hidden",
            'w-full sticky top-0 z-40',
          ].join(' ')}
        >
          <PdNavbar sticky={true} />
        </div>

        {/* navbar menu */}
        <div id="solution-simple-navbar" className={['hidden', 'laptop:block', 'w-full sticky top-0 z-30'].join(' ')}>
          <PdSimpleNavbar
            containerId="main"
            title={navbarMenu.title || ''}
            menus={navbarMenu.menus}
            link="/solutions"
          />
        </div>

        {/* topBanner */}
        <div className={clsx('w-full', '[aspect-ratio:3/4]', 'laptop:[aspect-ratio:16/9]')}>
          <PdSwiper slidesPerView={1} spaceBetween={0} autoplay={true} noSwiping={true} className="swiper-no-swiping">
            {!!data?.topBanner &&
              data.topBanner?.map((banner) => (
                <SwiperSlide key={banner.title}>
                  <PdMedia
                    src={isMobileDevice ? banner.mediaUrlH5 || banner.mediaUrl : banner.mediaUrl}
                    linkHref={banner?.jumpUrl || void 0}
                  >
                    <div className="w-full h-full flex justify-start items-center">
                      <div
                        className={[
                          'w-full mx-6 self-start mt-10 text-white',
                          'laptop:max-w-300 laptop:mx-auto laptop:self-center laptop:mt-0',
                        ].join(' ')}
                        style={{
                          color: banner.fontColor,
                        }}
                      >
                        <h3 className={['text-inherit', 'text-28', 'laptop:text-42', 'font-semibold'].join(' ')}>
                          {banner.title}
                        </h3>
                        <p className={['mt-4', 'laptop:mt-6 laptop:w-165', 'text-base'].join(' ')}>{banner.desc}</p>
                      </div>
                    </div>
                  </PdMedia>
                </SwiperSlide>
              ))}
          </PdSwiper>
        </div>

        {!!dataCardData?.length && (
          <Element name="section-1">
            <PdSection title={data?.middleTitle || ''}>
              <div
                className={[
                  'w-full px-6 gap-2',
                  'laptop:max-w-300 laptop:px-0 laptop:gap-4',
                  'max-w-screen mx-auto flex flex-wrap justify-center',
                ].join(' ')}
              >
                {dataCardData.map((d) => (
                  <PdDataCard
                    key={d.label}
                    label={d.label}
                    value={d.value}
                    icon={d.icon}
                    type={'WithIcon'}
                    className="flex-none laptop:flex-1"
                  />
                ))}
              </div>

              <p className="max-w-300 w-full px-6 laptop:px-0 text-left laptop:text-right text-xs text-black/40 font-normal mt-2">
                {data?.middleBottomDesc}
              </p>
            </PdSection>
          </Element>
        )}

        {!!data?.middleBanner && (
          <Element className="pb-72px bg-white" name="section-2">
            <PdSection
              title={data?.middleBanner?.title || ''}
              subtitle={data?.middleBanner?.desc || ''}
              bgWhite={true}
              bottomNoPadding={true}
            >
              <div
                className={clsx('w-full h-fit', 'px-0 [aspect-ratio:1/1]', 'laptop:px-12 laptop:[aspect-ratio:16/9]')}
              >
                <PdMedia
                  src={
                    isMobileDevice
                      ? data.middleBanner?.mediaUrlH5 || data.middleBanner?.mediaUrl
                      : data.middleBanner?.mediaUrl
                  }
                  mask={false}
                  showShadow={false}
                />
              </div>
            </PdSection>
          </Element>
        )}

        {/* 场景 ， 比如工业配送场景 section-3 */}
        {!!data?.secondSolutionModule?.secondSolutionItem?.length && (
          <Element name={'section-3'}>
            {showTabs && (
              <SlidingTabs
                type="row"
                tabEleClassName={clsx('laptop:!py-2')}
                tabsBoxClassName={clsx(
                  'w-full',
                  'py-4',
                  'laptop:py-8'
                  // 'bg-white/80 backdrop-saturate-150 backdrop-blur-lg'
                )}
                bgRefClassName={clsx('!h-36px', 'laptop:!h-10')}
                tabsContainerClassName="laptop:rounded-full"
                bgColor="bg-white mx-auto"
                tabLabels={data?.secondSolutionModule?.secondSolutionItem?.map((item) => item.title) ?? []}
                activeTabChange={handleActiveTabChange}
              />
            )}

            {activeData?.sceneItems?.map((sceneData, index) => (
              <PdSection key={index} title={''} className={clsx(showTabs ? '!pt-10 laptop:!pt-30' : '')}>
                <div className="w-full max-w-300 mx-auto">
                  <NewsDisplay
                    id={activeSecondSolutionItem + index}
                    title={sceneData.title}
                    images={sceneData.mediaUrl}
                    topcard={{
                      title: sceneData.topTitle,
                      text: sceneData.topContent,
                    }}
                    bottomcard={{
                      title: sceneData.bottomTitle,
                      text: sceneData.bottomContent,
                    }}
                  />

                  {!!sceneData.products?.length && (
                    <div
                      className={[
                        'mx-6 w-auto mt-4 p-4',
                        'laptop:w-full laptop:p-6 laptop:mt-4 laptop:mx-0',
                        'h-fit bg-white',
                      ].join(' ')}
                    >
                      {!!sceneData?.productTitle && (
                        <h4
                          className={[
                            'text-lg mb-6',
                            'laptop:text-2xl',
                            'w-full h-fit text-start font-semibold text-black/90',
                          ].join(' ')}
                        >
                          {sceneData?.productTitle}
                        </h4>
                      )}

                      <div className={clsx('grid', 'grid-cols-1 gap-8', 'laptop:grid-cols-2 laptop:gap-4')}>
                        {sceneData.products.map((item, index) => (
                          <div key={index} className={clsx('flex', 'flex-col gap-3', 'laptop:flex-row laptop:gap-4')}>
                            <div
                              className={clsx(
                                'flex-shrink-0 flex-grow-0 bg-gray-100',
                                'w-full h-55',
                                'laptop:w-186px laptop:h-55'
                              )}
                            >
                              <PdImage
                                src={item.mediaUrl || ''}
                                object="contain"
                                enableMask={false}
                                width={'100%'}
                                height={'100%'}
                              />
                            </div>

                            <div className={clsx('flex flex-col justify-between', 'laptop:py-2')}>
                              <div className={clsx('')}>
                                <div className={clsx('text-black/90 text-base font-semibold min-h-6')}>
                                  {item.title}
                                </div>
                                <div className={clsx('mt-2')}>
                                  <p
                                    className={clsx(
                                      'whitespace-pre-line',
                                      'text-black/60 font-normal !leading-normal',
                                      'text-base',
                                      'laptop:text-sm'
                                    )}
                                  >
                                    {item.content}
                                  </p>
                                </div>
                              </div>
                              <div className={clsx('mt-1', 'laptop:mt-0')}>
                                <Link href={item.jumpUrl}>
                                  <a className={['mt-6', 'laptop:mt-0', 'text-sm text-pdblue'].join(' ')}>
                                    {item?.moreTitle ?? t('KnowMore')}
                                    <AngleRight theme="PUDUBLUE" size="4x8" />
                                  </a>
                                </Link>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </PdSection>
            ))}

            {/* 板块 相关案列 Related Cases（当前二级行业下相关案列） */}
            {!!activeData?.customerCaseItems?.length && (
              <PdSection title={activeData?.customerCaseTitle}>
                {activeData?.customerCaseItems.map((item, index) => (
                  <RelatedCaseSection key={index} data={item} />
                ))}
              </PdSection>
            )}
          </Element>
        )}

        {/* 板块 案列下载 */}
        {!!data?.downloadItems?.length && (
          <Element name={'section-4'}>
            <PdSection
              title={data?.downloadTitle}
              bgWhite
              titleClassName="laptop:text-start laptop:mx-auto laptop:max-w-300 laptop:px-0"
            >
              <div className="w-auto laptop:w-full max-w-300 mx-6 laptop:mx-auto">
                <div className="grid laptop:grid-cols-2 gap-2 laptop:gap-4">
                  {data.downloadItems?.map((download) => (
                    <DownloadCard
                      key={download.title}
                      img={download.mediaUrl}
                      title={download.title}
                      desc={download.desc}
                      jumpUrl={download.downloadUrl}
                      downloadUrlTitle={download.downloadUrlTitle}
                    />
                  ))}
                </div>
              </div>
            </PdSection>
          </Element>
        )}

        {/* 板块 更多应用 More Applications */}
        {!!data?.otherSolutionItems?.length && (
          <Element name={'section-5'} className="overflow-hidden">
            <PdSection
              title={data?.otherSolutionTitle}
              titleClassName={isMobileDevice ? '' : '!px-0 flex justify-between items-end max-w-300'}
              titleAside={
                !isMobileDevice ? (
                  <Link href={'/solutions'}>
                    <a className="font-normal text-sm text-pdblue flex items-center">
                      {data.otherSolutionMoreTitle}
                      <AngleRight theme="PUDUBLUE" size="4x8" />
                    </a>
                  </Link>
                ) : null
              }
            >
              <div className="w-full laptop:w-300 px-6 laptop:px-0 laptop:mx-auto">
                <div className={clsx('w-full')}>
                  <SlidingTabContent data={data?.otherSolutionItems}>
                    <MoreApplicationsCard />
                  </SlidingTabContent>
                </div>
              </div>
            </PdSection>
          </Element>
        )}

        {/* {inChina && (
          <div className="w-full bg-white pb-40">
            <ContactSection showPdLocations={false} />
          </div>
        )} */}

        <PdFooter />
      </main>
    </div>
  )
}

export default Solution

export const getStaticProps = withSEO(
  async ({ locale, params }: GetStaticPropsContext) => {
    const messages = retriveMessages(['solutions'], locale as LocaleType)
    const region = findRegionByLocale(locale as string)

    const data = await getSolutionDetailsInternal({
      docId: (params?.solutionTitle as string) || '',
      region,
    })

    console.log('+++++++++getSolutionDetailsInternal++++++++++', data, {
      docId: (params?.solutionTitle as string) || '',
      region,
    })

    return {
      props: {
        messages,
        data,
      },
      revalidate: 60,
    }
  },
  ({ params }: GetStaticPropsContext) => {
    const solutionTitle = (params as { solutionTitle?: string })?.solutionTitle || ''
    return `/solutions/${solutionTitle}`
  }
)

/* 因为是dynamic routes, 并且使用了getStaticProps,所以一定需要定义 getStaticPaths */
export const getStaticPaths: GetStaticPaths<{ solutionTitle: string }> = async (context: GetStaticPathsContext) => {
  const solutionTitles = [
    'food-and-beverage',
    'retail',
    'hospitality',
    'industrial-warehouse-logistics',
    'health-care',
    'transportation-and-related-service',
    'entertainment-and-sports',
    'residential-and-office-buildings',
    'education',
    'public-service',
  ]

  const paths = solutionTitles.flatMap((solutionTitle) =>
    (context.locales || []).map((locale) => ({
      params: { solutionTitle },
      locale,
    }))
  )

  return {
    paths,
    fallback: 'blocking',
  }
}
